import { faArrowRight, faLock } from "@fortawesome/pro-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Link } from "~/components/Link";
import { Tooltip } from "~/components/Tooltip";
import { useUser } from "~/modules/user/hooks/useUser";
import { ColumnSpanConfig } from "~/types";
import { useRegistrationModal } from "~/modules/auth/hooks/useRegistrationModal";

// // // //

export function TableCard(props: {
    title: string;
    children: React.ReactNode;
    linkTo?: {
        label: string;
        href: string;
        analyticsLabel: string;
    };
    tooltip?: string;
    watermark?: boolean;
    button?: React.ReactNode;
    bordered?: boolean;
    colSpans?: ColumnSpanConfig;
    loginConfig?: {
        required: boolean;
        analyticsLabel: string;
        analyticsEventProperties: Record<string, string>;
    };
}) {
    const {
        title,
        children,
        tooltip,
        watermark = false,
        linkTo = null,
        button = null,
        loginConfig,
        bordered = true,
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
        },
    } = props;

    // Flag whether or not to show the column
    const { isLoggedIn } = useUser();
    const { open: openRegistrationModal } = useRegistrationModal();
    const loginRequired = loginConfig?.required || false;
    const isContentLoginGated = loginRequired && !isLoggedIn;

    const smColSpan = colSpans.sm || 12;
    const mdColSpan = colSpans.md || 12;
    const lgColSpan = colSpans.lg || 12;
    const xlColSpan = colSpans.xl || 6;
    const xxlColSpan = colSpans.xxl || 6;

    return (
        <div
            className={cn(
                "pt-5 bg-card rounded-lg shadow-xs border overflow-clip",
                "flex flex-col justify-start",
                `col-span-12 sm:col-span-${smColSpan} md:col-span-${mdColSpan} lg:col-span-${lgColSpan} xl:col-span-${xlColSpan} 2xl:col-span-${xxlColSpan}`,
            )}
        >
            <div className="flex flex-col gap-2 mb-5 md:flex-row md:justify-between md:items-center">
                <div className="w-full flex items-center gap-2 px-5">
                    <p className="text-foreground text-xl leading select-none">
                        {title}
                    </p>
                    {tooltip && (
                        <Tooltip
                            placement="right"
                            tooltipContent={
                                <p className="py-2 px-4 text-sm max-w-[20rem]">
                                    {tooltip}
                                </p>
                            }
                        >
                            {({ setTriggerRef }) => (
                                <span ref={setTriggerRef}>
                                    <FontAwesomeIcon
                                        icon={faQuestionCircle}
                                        className="text-slate-800/50 transition-colors h-4 cursor-help"
                                    />
                                </span>
                            )}
                        </Tooltip>
                    )}
                </div>
                {linkTo != null && (
                    <div className="px-5">
                        <Link
                            href={linkTo.href}
                            analyticsLabel={linkTo.analyticsLabel}
                            className="flex items-center gap-1.5 whitespace-nowrap"
                            variant="default"
                        >
                            {linkTo.label}
                            <FontAwesomeIcon
                                icon={faArrowRight}
                                className="h-4"
                            />
                        </Link>
                    </div>
                )}

                {button != null && <div className="px-5">{button}</div>}

                {isContentLoginGated && loginConfig != null && (
                    <div className="px-5">
                        <button
                            type="button"
                            onClick={() => {
                                openRegistrationModal({
                                    label: loginConfig.analyticsLabel,
                                    properties:
                                        loginConfig.analyticsEventProperties,
                                });
                            }}
                            className="select-none text-sm flex items-center gap-1.5 font-semibold whitespace-nowrap w-fit mt-0.5 text-link hover:text-link-hover transition-colors cursor-pointer"
                        >
                            <FontAwesomeIcon
                                icon={faLock}
                                className="h-2.5 w-2.5"
                            />
                            Log in to Unlock
                        </button>
                    </div>
                )}
            </div>

            <div
                className={cn(
                    "flex flex-col",
                    bordered && "border-t",
                    watermark && "tablecard-watermark",
                )}
            >
                {children}
            </div>
        </div>
    );
}
